import { FC, useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from './ui/button';
import { UserCheck, Users } from 'lucide-react';
import { useAuth0 } from '@auth0/auth0-react';
import { Input } from './ui/input';
import PhoneInput from './ui/phoneInput';
import useGetAgent from '@/hooks/useGetAgent';
import { useSearchParams } from 'react-router-dom';

interface Props {
  isOpen: boolean;
  setIsOpen: () => void;
}

const RegisterAccountModal: FC<Props> = ({ isOpen, setIsOpen }) => {
  const { loginWithRedirect } = useAuth0();
  const [query] = useSearchParams();

  const agentIdentifier = useMemo(() => {
    const agent = query.get('agent');
    if (agent) window.localStorage.setItem('agent', agent.replace(' ', '+'));
    const localAgent = window.localStorage.getItem('agent');
    return localAgent;
  }, [query]);

  const { data: agent } = useGetAgent(agentIdentifier);

  const [userDetails, setUserDetails] = useState<Record<string, string>>({});
  const [userType, setUserType] = useState(
    window.localStorage.getItem('user_type'),
  );

  const handleTypeSelect = (type: string) => {
    setUserType(type);
    window.localStorage.setItem('user_type', type);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setUserDetails((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = () => {
    window.localStorage.setItem('user', JSON.stringify(userDetails));

    loginWithRedirect({
      appState: {
        returnTo: '/dashboard?tab=reports',
      },
      authorizationParams: {
        screen_hint: 'signup',
      },
    });
  };

  useEffect(() => {
    if (agent) handleTypeSelect('USER');
  }, [agent]);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="sm:max-w-md">
        {!userType ? (
          <>
            <DialogHeader>
              <DialogTitle className="text-center text-2xl font-bold text-tax-deepBlue mb-2">
                Are You a Real Estate Agent?
              </DialogTitle>
              <DialogDescription className="text-center text-tax-neutralGray">
                Help us create the best experience for you.
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-4 mt-6">
              <Button
                onClick={() => handleTypeSelect('AGENT')}
                className="bg-primary hover:bg-primary/90 text-primary-foreground py-6 text-lg font-medium flex items-center justify-center gap-3"
              >
                <UserCheck className="w-5 h-5" />
                Yes, I'm a Real Estate Agent
              </Button>

              <Button
                onClick={() => handleTypeSelect('USER')}
                variant="outline"
                className="border-tax-borderGray hover:bg-tax-lightGray text-tax-neutralGray hover:text-tax-deepBlue py-6 text-lg font-medium flex items-center justify-center gap-3"
              >
                <Users className="w-5 h-5" />
                No, I'm a Property Owner
              </Button>
            </div>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="text-center text-2xl font-bold text-tax-deepBlue mb-2">
                Tell us about yourself.
              </DialogTitle>
            </DialogHeader>
            <form className="flex flex-col gap-4 mt-6">
              <Input
                id="firstName"
                name="firstName"
                value={userDetails.firstName}
                onChange={handleChange}
                placeholder="Your first name"
              />
              <Input
                id="lastName"
                name="lastName"
                value={userDetails.lastName}
                onChange={handleChange}
                placeholder="Your last name"
              />
              <PhoneInput
                name="phoneNumber"
                value={userDetails.phoneNumber}
                onChange={(name, value) => {
                  setUserDetails((prev) => ({
                    ...prev,
                    [name]: value,
                  }));
                }}
                placeholder="Your mobile number"
              />

              <Button
                onClick={(e) => {
                  e.preventDefault();

                  handleSubmit();
                }}
                className="bg-primary hover:bg-primary/90 text-primary-foreground py-6 text-lg font-medium flex items-center justify-center gap-3"
              >
                <Users className="w-5 h-5" />
                Submit
              </Button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default RegisterAccountModal;
